import React, { useState, useCallback, useRef } from 'react';
import proptypes from 'prop-types';
import { useImpression } from '@thd-olt-component-react/impression';
import { SwatchComponent } from './swatch-component';
import {
  getMouseEnterHandler, getMouseLeaveHandler,
  isMovingToPopover,
  shouldShowTooltipCondition, shouldShowUnavailableTooltipCondition,
  shouldShowOOSTooltipCondition
} from '../../super-sku-util';

const {
  bool, func, oneOf,
  shape, string, number
} = proptypes;

const getTitle = (shouldShowTooltip, value) => (shouldShowTooltip ? '' : value);
const shouldShowImpressionData = (index, isImpressionsRequired,
  isHovered, isClickedMobile) => (index !== 0 && isImpressionsRequired && (isHovered || isClickedMobile));

const createImpressionData = (swatchItemId, componentImpressionData) => ({
  id: swatchItemId || '',
  name: componentImpressionData?.name || '',
  component: componentImpressionData?.component || '',
  position: componentImpressionData?.position || 1,
  type: componentImpressionData?.type || '',
  category: 'ssku'
});

export const Swatch = (
  {
    channel,
    displayInToolTip = false,
    hoveredCombinationDetails = {},
    isAvailable,
    isSelected,
    isTooltipsEnabled = false,
    miniSwatch = false,
    onClick,
    onMouseEnter,
    onMouseLeave,
    swatchGuid,
    size = null,
    value,
    index = 0,
    swatchItemId = null,
    componentImpressionData = {
      name: '',
      component: '',
      position: 1,
      type: '',
    },
    isImpressionsRequired = false,
    isOOS = false,
    isUnavailable = false,
    unavailableTooltipMessage = '',
    oosTooltipMessage = ''
  }
) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClickedMobile, setIsClickedMobile] = useState(false);
  const anchorRef = useRef(null);

  const isMobile = channel === 'mobile';

  const mouseEnter = useCallback(
    getMouseEnterHandler(isMobile, setIsHovered, onMouseEnter, anchorRef),
    [isMobile, onMouseEnter]
  );

  const mouseLeave = useCallback(
    getMouseLeaveHandler(isMobile, setIsHovered, onMouseLeave, anchorRef),
    [isMobile, onMouseLeave]
  );

  const mouseLeaveEvent = useCallback(
    (event) => {
      if (isMovingToPopover(event)) return;
      mouseLeave(event);
    },
    [mouseLeave]
  );

  const handleClick = useCallback((event) => {
    onClick(event);
    if (isMobile) setIsClickedMobile(true);
  }, [onClick, isMobile]);

  const { ref: impressionRef, clickID: impressionClickID } = useImpression({
    data: createImpressionData(swatchItemId, componentImpressionData)
  });

  const isShowImpressionData = shouldShowImpressionData(index, isImpressionsRequired, isHovered, isClickedMobile);

  const impressionProps = isShowImpressionData ? {
    ref: impressionRef,
    clickid: impressionClickID
  } : {};

  const shouldShowOOSTTooltip = !!oosTooltipMessage && shouldShowOOSTooltipCondition(
    isHovered, isTooltipsEnabled, hoveredCombinationDetails, miniSwatch
  );

  const shouldShowUnavailableTooltip = !shouldShowOOSTTooltip
  && shouldShowUnavailableTooltipCondition(unavailableTooltipMessage,
    isHovered, isTooltipsEnabled, hoveredCombinationDetails, miniSwatch
  );
  const shouldShowTooltip = !shouldShowOOSTTooltip && !shouldShowUnavailableTooltip
  && shouldShowTooltipCondition(isHovered, isTooltipsEnabled, hoveredCombinationDetails, miniSwatch);
  const title = getTitle(shouldShowTooltip, value);

  return (
    <SwatchComponent
      value={value}
      isSelected={isSelected}
      isAvailable={isAvailable}
      miniSwatch={miniSwatch}
      swatchGuid={swatchGuid}
      displayInToolTip={displayInToolTip}
      title={title}
      onSwatchClick={handleClick}
      onSwatchMouseEnter={mouseEnter}
      onSwatchMouseLeave={mouseLeaveEvent}
      impressionProps={impressionProps}
      anchorRef={anchorRef}
      hoveredCombinationDetails={hoveredCombinationDetails}
      onPopoverClose={mouseLeave}
      showMainTooltip={shouldShowTooltip}
      showUnavailableTooltip={shouldShowUnavailableTooltip}
      shouldShowOOSTTooltip={shouldShowOOSTTooltip}
      isOOS={isOOS}
      isUnavailable={isUnavailable}
      unavailableTooltipMessage={unavailableTooltipMessage}
      oosTooltipMessage={oosTooltipMessage}
    />
  );
};

Swatch.displayName = 'Swatch';

Swatch.propTypes = {
  channel: oneOf(['desktop', 'mobile']).isRequired,
  displayInToolTip: bool,
  hoveredCombinationDetails: shape({}),
  isAvailable: bool.isRequired,
  isSelected: bool.isRequired,
  isTooltipsEnabled: bool,
  miniSwatch: bool,
  onClick: func.isRequired,
  onMouseEnter: func.isRequired,
  onMouseLeave: func.isRequired,
  size: string,
  swatchGuid: string.isRequired,
  value: string.isRequired,
  swatchItemId: string,
  index: number,
  componentImpressionData: shape({
    name: string,
    component: string,
    position: number,
    type: string,
  }),
  isImpressionsRequired: bool,
  isOOS: bool,
  isUnavailable: bool,
  unavailableTooltipMessage: string,
  oosTooltipMessage: string,
};